// client/src/components/tendermaker/BidStudioPanel.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";

// added for professional editor features used by EditorToolbar
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableCell } from "@tiptap/extension-table-cell";
import { Image } from "@tiptap/extension-image";
import { Color } from "@tiptap/extension-color";
import { TextStyle } from "@tiptap/extension-text-style";
import { Highlight } from "@tiptap/extension-highlight";
import mammoth from "mammoth";
import BidDocPane from "./BidDocPane";
import EditorPane from "./EditorPane";
import RequirementsChipStrip from "./RequirementsChipStrip";
import ConsolidationPanel from "./ConsolidationPanel";
import RequirementActionBar from "./RequirementActionBar";

import {
  tmProfileList,
  tmProfileMetaGet,
  tmProfileMetaSave,

  tmBidDocs,
  tmBidInitUpload,
  tmBidCommit,
  tmBidDocExtracted,

  tmRunItemsGet,
  tmRunItemsBulkUpsert,
  tmRunItemPatch,

  tmRunItemInitUpload,
  tmRunItemCommitUpload,
  tmRunItemDownload,
  tmRunItemPromoteToProfile,

  tmDraftSave,
  tmDraftGet,
  tmDocsMerge,
  tmDocsOverlay,
  tmDocsExtractPages,
  tmFileDownloadUrl,
  tmProxyPdfUrl,
  tmDocsRenderPdf,
} from "./tendermakerApi";

const box = { border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, background: "white" };
const subBox = { ...box, background: "#fafafa" };
const btn = {
  padding: "9px 12px",
  borderRadius: 10,
  border: "1px solid #e5e7eb",
  background: "#fff",
  cursor: "pointer",
  fontWeight: 800,
};
const btn2 = { ...btn, background: "#fafafa" };

function extOf(name = "") {
  const p = String(name).split(".").pop()?.toLowerCase() || "";
  return p;
}
function isPdfName(name = "") {
  return extOf(name) === "pdf";
}
function isDocxName(name = "") {
  return extOf(name) === "docx";
}

function findLatestByMasterId(profileDocs, masterId) {
  const arr = (profileDocs || []).filter(
    (d) => String(d.masterId || "").toLowerCase() === String(masterId).toLowerCase()
  );
  return arr[0] || null;
}

function safePre(text) {
  const t = String(text || "");
  const esc = t.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return `<pre style="white-space:pre-wrap;font-family:ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono','Courier New', monospace; font-size: 12.5px; background:#f9fafb; padding:12px; border-radius:10px; border:1px solid #e5e7eb;">${esc}</pre>`;
}

function escapeHtml(s = "") {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

// Normalize evidence entries into: { text, source?: { docId|fileId, page }, quote? }
function normalizeEvidenceItem(ev) {
  if (typeof ev === "string") return { text: ev, source: null };
  if (ev && typeof ev === "object") {
    const text = String(ev.text || ev.label || ev.title || ev.value || "").trim();
    const source = ev.source && typeof ev.source === "object" ? ev.source : null;
    const quote = ev.quote ? String(ev.quote) : "";
    if (text) return { text, source: source || null, quote };
  }
  return { text: String(ev || ""), source: null };
}

function normForSearch(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function tokenize(s = "") {
  return normForSearch(s)
    .replace(/[^a-z0-9 ]/g, " ")
    .split(" ")
    .filter((t) => t.length >= 3);
}

function scoreTokenOverlap(a, b) {
  const A = new Set(tokenize(a));
  const B = new Set(tokenize(b));
  if (!A.size || !B.size) return 0;
  let inter = 0;
  for (const t of A) if (B.has(t)) inter++;
  return inter / Math.min(A.size, B.size);
}

// robust exact + fuzzy page locator
function findBestPageForEvidence(pages, evidenceText) {
  const needle = normForSearch(evidenceText);
  if (!needle || needle.length < 5) return null;

  // 1) exact normalized contains
  for (const p of pages || []) {
    const hay = normForSearch(p?.text || "");
    if (hay && hay.includes(needle)) return Number(p?.page || p?.pageNumber || 1);
  }

  // 2) fuzzy fallback (OCR resilient)
  let best = { page: null, score: 0 };
  for (const p of pages || []) {
    const text = p?.text || "";
    const s = scoreTokenOverlap(evidenceText, text);
    if (s > best.score) {
      best = { page: Number(p?.page || p?.pageNumber || 1), score: s };
    }
  }

  return best.score >= 0.55 ? best.page : null;
}

export default function BidStudioPanel({ run }) {
  const [open, setOpen] = useState(true);
  const [err, setErr] = useState("");

  // profile
  const [profileDocs, setProfileDocs] = useState([]);
  const [meta, setMeta] = useState({ companyName: "", signingName: "", signingDesignation: "" });
  const [metaBusy, setMetaBusy] = useState(false);

  // bid docs
  const [bidDocs, setBidDocs] = useState([]);
  const bidFileRef = useRef(null);
  const [docsBusy, setDocsBusy] = useState(false);

  // run items
  const [items, setItems] = useState([]);
  const [activeItemId, setActiveItemId] = useState("");
  const [activeFileId, setActiveFileId] = useState(""); // tracks which file within item is active
  const [docTitle, setDocTitle] = useState("");
  const lastFilePerItemRef = useRef({}); // itemId -> fileId (remembers last active file per item)

  // selection for bulk ops
  const [selectedKeys, setSelectedKeys] = useState({}); // key -> true
  const [selectedBidKeys, setSelectedBidKeys] = useState({}); // bidDocKey -> true

  // editor pending content fix
  const [pendingHtml, setPendingHtml] = useState("");

  // Branding options
  const [branding, setBranding] = useState({
    includeLetterhead: true,
    includeSignature: true,
    includeStamp: true,
  });

  // User-added custom requirements (not from AI extraction)
  const [customReqs, setCustomReqs] = useState([]);

  // Evidence jump state (for clicking evidence chips → open PDF at page)
  const [jumpTo, setJumpTo] = useState(null); // { docId, page }
  const extractedCacheRef = useRef(new Map()); // docId -> { pages, fullText }

  // Inline preview for branded/merged PDFs (shown in left pane)
  const [previewPdfUrl, setPreviewPdfUrl] = useState("");

  // Right pane PDF viewer URL (for viewing requirement files as PDF)
  const [editorPdfUrl, setEditorPdfUrl] = useState("");
  // Track the R2 key of the PDF currently shown in editor (for branding targeting)
  const [editorPdfKey, setEditorPdfKey] = useState("");

  // Consolidation panel
  const [showConsolidate, setShowConsolidate] = useState(false);
  const [consolidateBusy, setConsolidateBusy] = useState(false);

  const bidId = run?.bidId || "";

  const reqs = useMemo(() => {
    const merged = run?.merged || {};
    const matched = Array.isArray(merged.matched_requirements) ? merged.matched_requirements : [];
    const annex = Array.isArray(merged.forms_annexures) ? merged.forms_annexures : [];
    const unknown = Array.isArray(merged.unknown_requirements) ? merged.unknown_requirements : [];

    const out = [];

    for (const x of matched) {
      const itemId = String(x.id || "").trim();
      if (!itemId) continue;

      const rawEv = Array.isArray(x.evidence) ? x.evidence : [];
      const normEv = rawEv.map(normalizeEvidenceItem);

      out.push({
        itemId,
        type: "requirement",
        label: x.label || x.id,
        evidence: normEv,
      });
    }

    annex.forEach((a, idx) => {
      const itemId = `annex_${idx + 1}`;
      out.push({
        itemId,
        type: "annexure",
        label: a.title || `Annexure ${idx + 1}`,
        evidence: a.note ? [normalizeEvidenceItem(a.note)] : [],
        annexureNote: a.note || "",
      });
    });

    unknown.forEach((u, idx) => {
      const itemId = `unknown_${idx + 1}`;
      const rawEv = Array.isArray(u.evidence) ? u.evidence : [];
      const normEv = rawEv.map(normalizeEvidenceItem);

      out.push({
        itemId,
        type: "unknown",
        label: u.text ? `Other: ${u.text.slice(0, 60)}${u.text.length > 60 ? "…" : ""}` : `Other ${idx + 1}`,
        evidence: normEv,
        unknownText: u.text || "",
      });
    });

    // Append user-added custom requirements
    for (const c of customReqs) {
      out.push({
        itemId: c.itemId,
        type: "custom",
        label: c.label,
        evidence: [],
      });
    }

    return out;
  }, [run, customReqs]);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({ openOnClick: false }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),

      // added for professional editor tools
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      Image,
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: `<p>Select a requirement and click <b>Edit Draft</b> or <b>AI Draft</b>.</p>`,
  });

  // Autosave draft every 15s — keyed on activeItemId + activeFileId
  const lastAutosaveRef = useRef({ itemId: "", fileId: "", html: "" });

  useEffect(() => {
    if (!editor) return;

    const t = setInterval(async () => {
      try {
        if (!activeItemId || !run?.id) return;

        const html = editor.getHTML();
        const prev = lastAutosaveRef.current;
        const saveKey = `${activeItemId}_${activeFileId}`;

        if (prev.itemId === activeItemId && prev.fileId === activeFileId && prev.html === html) return;

        const title = docTitle || reqs.find((r) => r.itemId === activeItemId)?.label || activeItemId;

        const saved = await tmDraftSave({ runId: run.id, itemId: activeItemId, title, html });

        await tmRunItemPatch(run.id, activeItemId, {
          removed: false,
          sourceType: "draft",
          sourceRef: { draftId: saved.id },
        });

        lastAutosaveRef.current = { itemId: activeItemId, fileId: activeFileId, html };
      } catch (e) {
        // keep silent to avoid UX noise on periodic autosave
      }
    }, 15000);

    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, activeItemId, activeFileId, docTitle, run?.id, reqs]);

  useEffect(() => {
    if (!editor) return;
    if (!pendingHtml) return;
    editor.commands.setContent(pendingHtml);
    setPendingHtml("");
  }, [editor, pendingHtml]);

  // Ctrl+S to save current draft
  useEffect(() => {
    function handleKeyDown(e) {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        if (activeItemId && editor && run?.id) {
          saveDraft(activeItemId).catch(() => {});
        }
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeItemId, editor, run?.id]);

  function getItem(itemId) {
    const docId = `${run.id}__${itemId}`;
    return items.find((x) => x.id === docId) || null;
  }

  async function reloadItems() {
    const got = await tmRunItemsGet(run.id);
    const data = got.data || [];
    setItems(data);
    return data;
  }

  async function ensureRunItems() {
    await tmRunItemsBulkUpsert(run.id, reqs);
    return await reloadItems();
  }

  async function loadVaultAndMeta() {
    const [pl, pm] = await Promise.all([tmProfileList(), tmProfileMetaGet()]);
    setProfileDocs(pl.data || []);
    if (pm?.data) {
      setMeta({
        companyName: pm.data.companyName || "",
        signingName: pm.data.signingName || "",
        signingDesignation: pm.data.signingDesignation || "",
      });
    }
  }

  async function loadBidDocs() {
    if (!bidId) return;
    const r = await tmBidDocs(bidId);
    setBidDocs(r.data || []);
  }

  useEffect(() => {
    if (!run?.id) return;
    setErr("");
    (async () => {
      await loadVaultAndMeta();
      const loadedItems = await ensureRunItems();
      if (bidId) await loadBidDocs();

      // Reconstruct custom requirements from persisted Firestore items
      const existingCustom = (loadedItems || [])
        .filter((it) => it.itemId && it.itemId.startsWith("custom_") && it.label)
        .map((it) => ({ itemId: it.itemId, label: it.label }));
      if (existingCustom.length > 0) {
        setCustomReqs(existingCustom);
      }
    })().catch((e) => setErr(String(e?.message || e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run?.id]);

  async function saveMeta() {
    setMetaBusy(true);
    setErr("");
    try {
      await tmProfileMetaSave(meta);
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setMetaBusy(false);
    }
  }

  async function toggleRemove(itemId) {
    const cur = getItem(itemId);
    const next = !cur?.removed;
    await tmRunItemPatch(run.id, itemId, { removed: next });
    await reloadItems();
  }

  async function addCustomRequirement(label) {
    if (!label || !label.trim()) return;
    const ts = Date.now();
    const itemId = `custom_${ts}`;
    setCustomReqs((prev) => [...prev, { itemId, label: label.trim() }]);
    // Create the item in Firestore so it persists
    try {
      await tmRunItemPatch(run.id, itemId, { removed: false, label: label.trim() });
      await reloadItems();
    } catch (e) {
      setErr(String(e?.message || e));
    }
  }

  async function attachVault(itemId, profileDocId) {
    const d = profileDocs.find((x) => x.id === profileDocId);
    if (!d?.key) {
      setErr("Selected profile doc has no key. Refresh profile list.");
      return;
    }
    await tmRunItemPatch(run.id, itemId, {
      removed: false,
      sourceType: "vault",
      sourceRef: { docId: profileDocId, key: d.key, fileName: d.fileName || null, contentType: d.contentType || null },
      filesAppend: [{
        fileName: d.fileName || profileDocId,
        contentType: d.contentType || "application/octet-stream",
        key: d.key,
        type: "vault",
      }],
    });
    await reloadItems();
  }

  async function uploadForItem(itemId, file) {
    if (!file) return;
    if (!run?.id) return setErr("No active run. Load a run first.");
    if (!itemId) return setErr("No requirement selected.");
    setErr("");
    const fileName = file.name;
    const contentType = file.type || "application/octet-stream";

    const init = await tmRunItemInitUpload({ runId: run.id, itemId, fileName, contentType });

    const put = await fetch(init.putUrl, {
      method: "PUT",
      headers: { "Content-Type": contentType },
      body: file,
    });
    if (!put.ok) throw new Error(`Upload failed: ${put.status}`);

    await tmRunItemCommitUpload({ runId: run.id, itemId, key: init.key, fileName, contentType });
    await reloadItems();

    if (isDocxName(fileName)) {
      await loadDocxFileToEditor(file, `${reqs.find((r) => r.itemId === itemId)?.label || itemId} (from DOCX)`, itemId);
    }
  }

  // Upload multiple files and append to item's files[] array (stores in R2)
  async function uploadFilesForItem(itemId, fileList) {
    if (!fileList?.length) return;
    if (!run?.id) return setErr("No active run. Load a run first.");
    if (!itemId) return setErr("No requirement selected.");
    setErr("");
    try {
      const newFiles = [];
      for (const file of Array.from(fileList)) {
        const fileName = file.name;
        const contentType = file.type || "application/octet-stream";
        // Get signed R2 upload URL
        const init = await tmRunItemInitUpload({ runId: run.id, itemId, fileName, contentType });
        // Upload directly to R2
        const put = await fetch(init.putUrl, {
          method: "PUT",
          headers: { "Content-Type": contentType },
          body: file,
        });
        if (!put.ok) throw new Error(`Upload failed for ${fileName}: ${put.status}`);
        newFiles.push({ fileName, contentType, key: init.key, type: "upload" });
      }
      // Append all uploaded files to files[] array in one patch
      if (newFiles.length) {
        await tmRunItemPatch(run.id, itemId, {
          removed: false,
          filesAppend: newFiles,
        });
      }
      await reloadItems();
    } catch (e) {
      setErr(String(e?.message || e));
    }
  }

  // Download a specific file from files[] by its R2 key
  async function downloadFile(itemId, file) {
    if (!file?.key) return setErr("No file key for download.");
    try {
      const r = await tmFileDownloadUrl(file.key);
      if (r?.url) window.open(r.url, "_blank");
    } catch (e) {
      setErr(String(e?.message || e));
    }
  }

  // View a specific file — PDF opens in right pane PDF viewer, DOCX loads into editor
  async function viewFile(itemId, file) {
    if (!file?.key) return setErr("No file key to view.");
    setActiveFileId(file.fileId || "");
    lastFilePerItemRef.current[itemId] = file.fileId || "";
    try {
      const r = await tmFileDownloadUrl(file.key);
      if (!r?.url) return setErr("Could not get download URL.");
      if (isPdfName(file.fileName)) {
        // Show PDF in right pane viewer and track its key for branding
        setEditorPdfUrl(tmProxyPdfUrl(r.url));
        setEditorPdfKey(file.key);
      } else if (isDocxName(file.fileName)) {
        setEditorPdfUrl(""); // clear PDF viewer
        setEditorPdfKey("");
        const resp = await fetch(r.url);
        if (!resp.ok) throw new Error(`Download failed: ${resp.status}`);
        const blob = await resp.blob();
        await loadDocxFileToEditor(blob, `${file.fileName} (from attached DOCX)`, itemId);
      } else {
        window.open(r.url, "_blank");
      }
    } catch (e) {
      setErr(String(e?.message || e));
    }
  }

  // Remove a specific file from item's files[] array
  async function removeFile(itemId, fileId) {
    if (!fileId) return;
    try {
      await tmRunItemPatch(run.id, itemId, { filesRemove: [fileId] });
      await reloadItems();
    } catch (e) {
      setErr(String(e?.message || e));
    }
  }

  // Create a new blank document for a requirement
  function addNewDoc(itemId, label) {
    setActiveItemId(itemId);
    setActiveFileId("");
    setDocTitle(label ? `${label} - New Document` : "New Document");
    if (editor) {
      editor.commands.setContent("<p>Start typing your document here...</p>");
      lastAutosaveRef.current = { itemId, fileId: "", html: "" }; // force autosave on next cycle
    } else {
      setPendingHtml("<p>Start typing your document here...</p>");
    }
  }

  // Merge selected PDF files within a single requirement into one PDF
  // Opens result in right pane and saves to files[]
  async function mergeItemFiles(itemId, selectedPdfKeys) {
    if (!selectedPdfKeys || selectedPdfKeys.length < 2) return setErr("Select at least 2 PDF files to merge.");
    try {
      const r = await tmDocsMerge({ keys: selectedPdfKeys, runId: run.id });
      if (r?.url) {
        // Show in right pane PDF viewer
        setEditorPdfUrl(r.url);
        if (r.key) {
          setEditorPdfKey(r.key);
          // Save merged PDF to item's files[]
          await tmRunItemPatch(run.id, itemId, {
            filesAppend: [{
              fileName: `merged_${Date.now()}.pdf`,
              contentType: "application/pdf",
              key: r.key,
              type: "merged",
            }],
          });
          await reloadItems();
        }
      }
    } catch (e) {
      setErr(String(e?.message || e));
    }
  }

  // Rename a file in an item's files[] array
  async function renameFile(itemId, fileId, newFileName) {
    if (!fileId || !newFileName) return;
    try {
      await tmRunItemPatch(run.id, itemId, {
        filesRename: [{ fileId, fileName: newFileName }],
      });
      await reloadItems();
    } catch (e) {
      setErr(String(e?.message || e));
    }
  }

  // Apply branding (sign/stamp/letterhead) to a file inline in the right pane
  // Uses the branding checkbox state for combo selection
  async function applyBrandingInline(itemId) {
    // Use the tracked editorPdfKey — this is the PDF currently displayed in the right pane
    const targetKey = editorPdfKey;
    if (!targetKey) return setErr("No PDF is currently open in the editor. View a PDF first.");
    const { signatureKey, stampKey, headerKey, footerKey } = getSigStampKeys();

    if (branding.includeSignature && !signatureKey) return setErr("Upload signature in Profile Vault first (masterId: signature).");
    if (branding.includeStamp && !stampKey) return setErr("Upload stamp in Profile Vault first (masterId: stamp).");
    if (branding.includeLetterhead && !(headerKey || footerKey)) return setErr("Upload letterhead in Profile Vault first.");
    if (!branding.includeSignature && !branding.includeStamp && !branding.includeLetterhead) return setErr("Select at least one: Signature, Stamp, or Letterhead.");

    try {
      const out = await tmDocsOverlay({
        inputKeys: [targetKey],
        runId: run.id,
        signatureKey: branding.includeSignature ? signatureKey : null,
        stampKey: branding.includeStamp ? stampKey : null,
        headerKey: branding.includeLetterhead ? (headerKey || null) : null,
        footerKey: branding.includeLetterhead ? (footerKey || null) : null,
      });
      const results = (out?.data || []).filter((x) => x.url);
      if (!results.length) return setErr("No output URLs returned.");
      // Show result in right pane PDF viewer and track new key
      setEditorPdfUrl(results[0].url);
      if (results[0].key) {
        setEditorPdfKey(results[0].key);
        // Add branded PDF to item's files[]
        if (itemId) {
          await tmRunItemPatch(run.id, itemId, {
            filesAppend: [{
              fileName: `branded_${Date.now()}.pdf`,
              contentType: "application/pdf",
              key: results[0].key,
              type: "branded",
            }],
          });
          await reloadItems();
        }
      }
    } catch (e) {
      setErr(String(e?.message || e));
    }
  }

  // Save current draft as PDF and add to item's files[]
  async function saveDraftAsPdf(itemId) {
    if (!editor || !itemId) return;
    setErr("");
    try {
      const html = editor.getHTML();
      const title = docTitle || reqs.find((r) => r.itemId === itemId)?.label || itemId;
      const rendered = await tmDocsRenderPdf({
        runId: run.id,
        itemId,
        title,
        html,
      });
      if (rendered?.key) {
        await tmRunItemPatch(run.id, itemId, {
          filesAppend: [{
            fileName: `${title.replace(/[^a-zA-Z0-9_\- ]/g, "").slice(0, 50)}.pdf`,
            contentType: "application/pdf",
            key: rendered.key,
            type: "draft",
          }],
        });
        await reloadItems();
        // Show the rendered PDF in the right pane and track key
        if (rendered.url) {
          setEditorPdfUrl(rendered.url);
          setEditorPdfKey(rendered.key);
        }
      }
    } catch (e) {
      setErr(String(e?.message || e));
    }
  }

  // Extract specific pages from a bid doc PDF and add to requirement's files[]
  async function addBidDocPages(itemId, bidDoc, pages) {
    if (!itemId || !bidDoc?.key) return setErr("No document key for page extraction.");
    setErr("");
    try {
      const result = await tmDocsExtractPages({
        key: bidDoc.key,
        runId: run.id,
        pages,
      });
      if (!result?.key) return setErr("Page extraction returned no key.");
      const pagesLabel = pages.join(",");
      const baseName = (bidDoc.fileName || "doc").replace(/\.pdf$/i, "");
      await tmRunItemPatch(run.id, itemId, {
        filesAppend: [{
          fileName: `${baseName}_p${pagesLabel}.pdf`,
          contentType: "application/pdf",
          key: result.key,
          type: "bid_content",
        }],
      });
      await reloadItems();
    } catch (e) {
      setErr(String(e?.message || e));
    }
  }

  // Add a bid document directly to a requirement's files[] array (copies its R2 key)
  async function addBidDocToItem(itemId, bidDoc) {
    if (!itemId || !bidDoc) return;
    const key = bidDoc.key || "";
    const fileName = bidDoc.fileName || bidDoc.id || "bid_doc";
    const contentType = bidDoc.contentType || "application/pdf";
    if (!key) {
      setErr("Bid document has no storage key.");
      return;
    }
    try {
      await tmRunItemPatch(run.id, itemId, {
        filesAppend: [{
          fileName,
          contentType,
          key,
          type: "bid_content",
        }],
      });
      await reloadItems();
    } catch (e) {
      setErr(String(e?.message || e));
    }
  }

  async function downloadItemFile(itemId) {
    const it = getItem(itemId);
    const srcKey = it?.sourceRef?.key;
    if (!srcKey) {
      setErr("No file attached for download.");
      return;
    }
    const r = await tmRunItemDownload({ runId: run.id, itemId });
    if (r?.url) window.open(r.url, "_blank");
  }

  async function promoteItemToProfile(itemId) {
    const it = getItem(itemId);
    if (!it?.sourceRef?.key) {
      setErr("Attach/upload a file first, then promote.");
      return;
    }
    const masterId = prompt(
      "Promote to Profile Vault under masterId (e.g. turnover_ca, emd, iso, msme, oem_auth)?",
      "general"
    );
    if (masterId === null) return;
    const label = prompt("Label for Profile Vault entry:", it.label || itemId);
    if (label === null) return;

    await tmRunItemPromoteToProfile({ runId: run.id, itemId, masterId, label });
    await loadVaultAndMeta();
  }

  async function saveDraft(itemId) {
    if (!editor) return;
    setErr("");
    try {
      const title = docTitle || reqs.find((r) => r.itemId === itemId)?.label || itemId;
      const html = editor.getHTML();
      const r = await tmDraftSave({ runId: run.id, itemId, title, html });

      await tmRunItemPatch(run.id, itemId, {
        removed: false,
        sourceType: "draft",
        sourceRef: { draftId: r.id },
      });

      // Update autosave ref so we know this content is saved
      lastAutosaveRef.current = { itemId, fileId: activeFileId, html };

      await reloadItems();
    } catch (e) {
      setErr(String(e?.message || e));
    }
  }

  // edit button helper — sets active requirement for editing, clears PDF mode
  function onEditDraft(itemId, label) {
    setActiveItemId(itemId);
    setDocTitle(label || itemId);
    setEditorPdfUrl("");
    setEditorPdfKey("");
  }

  // Select requirement from chip strip — auto-save current, load existing draft
  async function selectRequirement(itemId) {
    // 1) Auto-save current draft if dirty (always save, not just when changed)
    if (activeItemId && editor && run?.id && !editorPdfUrl) {
      try {
        const html = editor.getHTML();
        const title = docTitle || reqs.find((r) => r.itemId === activeItemId)?.label || activeItemId;
        // Always save on switch to prevent data loss
        const saved = await tmDraftSave({ runId: run.id, itemId: activeItemId, title, html });
        await tmRunItemPatch(run.id, activeItemId, {
          removed: false,
          sourceType: "draft",
          sourceRef: { draftId: saved.id },
        });
        lastAutosaveRef.current = { itemId: activeItemId, fileId: activeFileId, html };
      } catch {
        // silent — don't block switching
      }
    }

    // Remember last file for current item
    if (activeItemId && activeFileId) {
      lastFilePerItemRef.current[activeItemId] = activeFileId;
    }

    // 2) Set new active
    const req = reqs.find((r) => r.itemId === itemId);
    setActiveItemId(itemId);
    setDocTitle(req?.label || itemId);

    // 3) Determine which file to resume
    const item = getItem(itemId);
    const files = Array.isArray(item?.files) ? item.files : [];
    const lastFileId = lastFilePerItemRef.current[itemId] || "";
    const resumeFile = lastFileId ? files.find((f) => f.fileId === lastFileId) : null;

    // 4) If resuming a PDF file, show it in the PDF viewer
    if (resumeFile && isPdfName(resumeFile.fileName) && resumeFile.key) {
      setActiveFileId(resumeFile.fileId);
      try {
        const r = await tmFileDownloadUrl(resumeFile.key);
        if (r?.url) {
          setEditorPdfUrl(tmProxyPdfUrl(r.url));
          setEditorPdfKey(resumeFile.key);
        }
      } catch { /* silent */ }
      return;
    }

    // 5) Load existing draft content
    setActiveFileId("");
    setEditorPdfUrl("");
    setEditorPdfKey("");

    if (item?.sourceType === "draft" && item?.sourceRef?.draftId) {
      try {
        const draft = await tmDraftGet(item.sourceRef.draftId);
        const html = draft?.html || "<p></p>";
        if (editor) {
          editor.commands.setContent(html);
          lastAutosaveRef.current = { itemId, fileId: "", html };
        } else {
          setPendingHtml(html);
        }
      } catch {
        if (editor) editor.commands.setContent("<p>Failed to load draft. Start typing to create a new one.</p>");
      }
    } else {
      // Show evidence summary or placeholder
      const ev = (req?.evidence || []).map((e) => e.text).filter(Boolean);
      const html = ev.length > 0
        ? `<p><b>Evidence from tender (review &amp; edit):</b></p>${safePre(ev.join("\n\n"))}`
        : `<p>No draft yet. Start typing.</p>`;
      if (editor) {
        editor.commands.setContent(html);
        lastAutosaveRef.current = { itemId, fileId: "", html };
      } else {
        setPendingHtml(html);
      }
    }
  }

  function loadEvidenceToEditor(itemId) {
    const r = reqs.find((x) => x.itemId === itemId);
    const txt = (r?.evidence || []).map((e) => e.text).filter(Boolean).join("\n\n");
    const html = txt
      ? `<p><b>Extracted from tender (review & edit):</b></p>${safePre(txt)}`
      : `<p>(No extracted text available yet.)</p>`;

    if (!editor) setPendingHtml(html);
    else editor.commands.setContent(html);

    setActiveItemId(itemId);
    setDocTitle(r?.label || itemId);
  }

  async function loadDocxFileToEditor(fileOrBlob, title, itemId) {
    try {
      const ab = await fileOrBlob.arrayBuffer();
      const result = await mammoth.extractRawText({ arrayBuffer: ab });
      const text = (result?.value || "").trim();
      const html = text
        ? `<p><b>${title}</b></p>${safePre(text)}`
        : `<p>(DOCX loaded, but no readable text found.)</p>`;

      if (!editor) setPendingHtml(html);
      else editor.commands.setContent(html);

      setActiveItemId(itemId || "");
      setDocTitle(title || "");
    } catch (e) {
      setErr(`DOCX read failed: ${String(e?.message || e)}`);
    }
  }

  // Load content from bid document viewer into TipTap editor
  function loadContentToEditor(html, title) {
    if (!editor) setPendingHtml(html);
    else editor.commands.setContent(html);
    if (title) setDocTitle(title);
  }

  async function loadAttachedDocToEditor(itemId) {
    const it = getItem(itemId);
    const fileName = it?.sourceRef?.fileName || "";
    if (!it?.sourceRef?.key) return setErr("No attachment to load.");

    if (isPdfName(fileName)) {
      setErr("PDF cannot be loaded from requirement attachment directly. Use bid doc Split View / PDF → Editor.");
      return;
    }

    if (!isDocxName(fileName)) {
      setErr("Only DOCX can be loaded into editor right now.");
      return;
    }

    const dl = await tmRunItemDownload({ runId: run.id, itemId });
    if (!dl?.url) return setErr("Could not get download URL.");

    const resp = await fetch(dl.url);
    if (!resp.ok) return setErr(`Download failed: ${resp.status}`);
    const blob = await resp.blob();

    await loadDocxFileToEditor(blob, `${reqs.find((r) => r.itemId === itemId)?.label || itemId} (from attached DOCX)`, itemId);
  }

  // Minimal-safe placeholder unless profile download endpoint exists
  async function insertVaultImage(masterId) {
    try {
      setErr("");
      const doc = findLatestByMasterId(profileDocs, masterId);
      if (!doc?.key) {
        setErr(`No document found in Profile Vault for masterId: ${masterId}`);
        return;
      }
      setErr(`Insert-from-vault image for "${masterId}" requires profile-doc signed URL endpoint wiring.`);
    } catch (e) {
      setErr(String(e?.message || e));
    }
  }

  // ====== PDF OPS
  function getSigStampKeys() {
    const sig = findLatestByMasterId(profileDocs, "signature");
    const stp = findLatestByMasterId(profileDocs, "stamp");
    const hdr = findLatestByMasterId(profileDocs, "letterhead_header");
    const ftr = findLatestByMasterId(profileDocs, "letterhead_footer");

    return {
      signatureKey: sig?.key || "",
      stampKey: stp?.key || "",
      headerKey: hdr?.key || "",
      footerKey: ftr?.key || "",
    };
  }

  async function applyBrandingToKeys(inputKeys) {
    const { signatureKey, stampKey, headerKey, footerKey } = getSigStampKeys();

    if (!inputKeys.length) {
      setErr("Select/attach at least 1 PDF.");
      return;
    }

    if (branding.includeSignature && !signatureKey) {
      setErr("Upload signature in Profile Vault first (masterId: signature).");
      return;
    }
    if (branding.includeStamp && !stampKey) {
      setErr("Upload stamp in Profile Vault first (masterId: stamp).");
      return;
    }
    if (branding.includeLetterhead && !(headerKey || footerKey)) {
      setErr("Upload letterhead header/footer in Profile Vault first (masterId: letterhead_header, letterhead_footer).");
      return;
    }

    const out = await tmDocsOverlay({
      inputKeys,
      runId: run.id,
      signatureKey: branding.includeSignature ? signatureKey : null,
      stampKey: branding.includeStamp ? stampKey : null,
      headerKey: branding.includeLetterhead ? headerKey || null : null,
      footerKey: branding.includeLetterhead ? footerKey || null : null,
    });

    const results = (out?.data || []).filter((x) => x.url);
    if (!results.length) {
      setErr("No output URLs returned.");
      return;
    }
    // Show first result inline in the left pane; if multiple, open extras in new tabs
    setPreviewPdfUrl(results[0].url);
    if (results.length > 1) {
      results.slice(1).forEach((x) => window.open(x.url, "_blank"));
    }
  }

  async function mergeKeys(keys) {
    if (!keys.length) return setErr("Select at least 2 PDFs to merge.");
    if (keys.length < 2) return setErr("Select at least 2 PDFs to merge.");
    const r = await tmDocsMerge({ keys, runId: run.id });
    if (r?.url) {
      setEditorPdfUrl(r.url);
      if (r.key) {
        setEditorPdfKey(r.key);
        if (activeItemId) {
          await tmRunItemPatch(run.id, activeItemId, {
            filesAppend: [{
              fileName: `merged_${Date.now()}.pdf`,
              contentType: "application/pdf",
              key: r.key,
              type: "merged",
            }],
          });
          await reloadItems();
        }
      }
      setShowConsolidate(false); // auto-close panel to show result
    }
  }

  // Render drafts to PDF, then merge with existing PDF keys
  async function renderAndMerge(pdfKeys, draftIds) {
    setConsolidateBusy(true);
    setErr("");
    try {
      const allKeys = [...pdfKeys];

      // Render each draft to PDF and collect keys
      for (const draftId of draftIds) {
        const draft = await tmDraftGet(draftId);
        if (!draft?.html) continue;
        const rendered = await tmDocsRenderPdf({
          runId: run.id,
          itemId: draft.itemId || "consolidation",
          title: draft.title || "Draft",
          html: draft.html,
        });
        if (rendered?.key) allKeys.push(rendered.key);
      }

      if (allKeys.length < 1) {
        setErr("No renderable content found.");
        return;
      }
      if (allKeys.length === 1) {
        // Just one PDF — show it directly in right pane
        const r = await tmFileDownloadUrl(allKeys[0]);
        if (r?.url) {
          setEditorPdfUrl(tmProxyPdfUrl(r.url));
          setEditorPdfKey(allKeys[0]);
          setShowConsolidate(false);
        }
        return;
      }

      const r = await tmDocsMerge({ keys: allKeys, runId: run.id });
      if (r?.url) {
        // Show in right pane
        setEditorPdfUrl(r.url);
        if (r.key) {
          setEditorPdfKey(r.key);
          // Save merged PDF to a "consolidation" pseudo-item files[]
          // Use the first selected item or a generic key
          if (activeItemId) {
            await tmRunItemPatch(run.id, activeItemId, {
              filesAppend: [{
                fileName: `consolidated_${Date.now()}.pdf`,
                contentType: "application/pdf",
                key: r.key,
                type: "merged",
              }],
            });
            await reloadItems();
          }
        }
        setShowConsolidate(false); // auto-close panel to show result
      }
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setConsolidateBusy(false);
    }
  }

  function toggleKeySelection(mapSetter, map, key) {
    mapSetter((prev) => {
      const next = { ...(prev || {}) };
      if (next[key]) delete next[key];
      else next[key] = true;
      return next;
    });
  }

  async function uploadBidDocs(files) {
    if (!bidId) return;
    if (!files?.length) return;

    setDocsBusy(true);
    setErr("");
    try {
      for (const f of Array.from(files)) {
        const fileName = f.name;
        const contentType = f.type || "application/octet-stream";
        const init = await tmBidInitUpload({ bidId, fileName, contentType });

        const put = await fetch(init.putUrl, {
          method: "PUT",
          headers: { "Content-Type": contentType },
          body: f,
        });
        if (!put.ok) throw new Error(`Bid doc upload failed: ${put.status}`);

        await tmBidCommit({ bidId, key: init.key, fileName, contentType });

        if (isDocxName(fileName)) {
          await loadDocxFileToEditor(f, `Bid Doc: ${fileName} (from DOCX)`, "");
        }
      }
      await loadBidDocs();
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setDocsBusy(false);
      if (bidFileRef.current) bidFileRef.current.value = "";
    }
  }

  async function getExtractedPagesForDoc(doc) {
    if (!bidId || !doc?.id) return [];
    const cached = extractedCacheRef.current.get(doc.id);
    if (cached?.pages) return cached.pages;

    const r = await tmBidDocExtracted(bidId, doc.id);
    const pages = r?.pages || r?.data?.pages || [];
    const fullText = r?.fullText || r?.data?.fullText || "";
    extractedCacheRef.current.set(doc.id, { pages, fullText });
    return pages;
  }

  // Evidence click — find doc + page, then jump to it in inline PDF viewer
  async function onClickEvidence(ev) {
    try {
      setErr("");
      const src = ev?.source;
      const evText = String(ev?.text || "").trim();
      if (!evText && !(src?.page && (src?.docId || src?.fileId))) return;

      // 1) If model gave source: use it directly
      if (src?.page && (src?.docId || src?.fileId)) {
        const page = Number(src.page || 1);
        const docId = src.docId || src.fileId;
        const docObj = bidDocs.find((d) => d.id === docId);
        if (!docObj) {
          setErr("Evidence source doc not found in Bid Documents list. Upload it first.");
          return;
        }
        setJumpTo({ docId: docObj.id, page });
        return;
      }

      // 2) Search across all PDF bid docs for evidence text
      const pdfDocs = (bidDocs || []).filter((d) => isPdfName(d.fileName || d.id));
      if (!pdfDocs.length) {
        setErr("No PDF bid documents available to search. Upload the tender PDF(s) first.");
        return;
      }

      const matches = [];
      for (const d of pdfDocs) {
        const pages = await getExtractedPagesForDoc(d);
        const p = findBestPageForEvidence(pages, evText);
        if (p) matches.push({ doc: d, page: p });
      }

      if (matches.length >= 1) {
        setJumpTo({ docId: matches[0].doc.id, page: matches[0].page });
        return;
      }

      setErr("Could not locate this evidence text in extracted pages. Backend evidence→page mapping will make this perfect.");
    } catch (e) {
      setErr(String(e?.message || e));
    }
  }

  const activeList = useMemo(() => {
    const removed = new Set(items.filter((i) => i.removed).map((i) => i.itemId));
    return reqs.filter((r) => !removed.has(r.itemId));
  }, [reqs, items]);

  const selectedReqPdfKeys = useMemo(() => Object.keys(selectedKeys || {}), [selectedKeys]);
  const selectedBidPdfKeys = useMemo(() => Object.keys(selectedBidKeys || {}), [selectedBidKeys]);

  return (
    <div style={{ ...box, marginTop: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontWeight: 900 }}>Bid Studio</div>
          <div style={{ color: "#6b7280", fontSize: 13 }}>
            2-pane workspace • Inline PDF/Word viewer • Evidence jump • Professional editor tools
          </div>
        </div>
        <button style={btn2} onClick={() => setOpen((v) => !v)}>
          {open ? "Hide" : "Open"}
        </button>
      </div>

      {!open ? null : (
        <>
          {err ? <div style={{ color: "crimson", marginTop: 10 }}>{err}</div> : null}

          {/* compact top status */}
          <div style={{ ...subBox, marginTop: 12, padding: 10 }}>
            <div style={{ display: "flex", gap: 10, justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
              <div style={{ color: "#6b7280", fontSize: 12 }}>
                Run: <b style={{ color: "#111827" }}>{run?.id || "-"}</b> • Bid: <b style={{ color: "#111827" }}>{bidId || "-"}</b> •
                Requirements: <b style={{ color: "#111827" }}>{activeList.length}</b>
              </div>
              <div style={{ color: "#6b7280", fontSize: 12 }}>
                Active draft: <b style={{ color: "#111827" }}>{activeItemId || "(none selected)"}</b>
              </div>
            </div>
          </div>

          {/* ===== 2-PANE LAYOUT ===== */}
          <div
            style={{
              ...box,
              marginTop: 8,
              padding: 10,
              height: "calc(100vh - 200px)",
              minHeight: 560,
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
              alignItems: "stretch",
            }}
          >
            {/* LEFT: Chip strip + Bid documents + inline viewer */}
            <div style={{ minWidth: 0, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ flex: "0 0 auto" }}>
                <RequirementsChipStrip
                  allReqs={reqs}
                  items={items}
                  activeItemId={activeItemId}
                  getItem={getItem}
                  onSelectRequirement={selectRequirement}
                  onToggleRemove={toggleRemove}
                  onAddRequirement={addCustomRequirement}
                  onConsolidate={() => setShowConsolidate(true)}
                />
              </div>
              <div style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
              <BidDocPane
                bidId={bidId}
                bidDocs={bidDocs}
                docsBusy={docsBusy}
                activeItemId={activeItemId}
                jumpTo={jumpTo}
                onSetJumpTo={setJumpTo}
                onUploadBidDocs={uploadBidDocs}
                onLoadBidDocs={loadBidDocs}
                onAddBidDocToItem={addBidDocToItem}
                onAddBidDocPages={addBidDocPages}
                onGetExtractedPages={getExtractedPagesForDoc}
                onSetErr={setErr}
                previewPdfUrl={previewPdfUrl}
                onClearPreview={() => setPreviewPdfUrl("")}
              />
            </div>
            </div>

            {/* RIGHT: Action Bar (compact) + Editor (large) */}
            <div style={{ minWidth: 0, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column", gap: 4 }}>
              {/* Requirement action bar — compact top strip */}
              <div style={{ flex: "0 0 auto", maxHeight: "30%", overflowY: "auto" }}>
                <RequirementActionBar
                  requirement={activeItemId ? reqs.find((r) => r.itemId === activeItemId) : null}
                  item={activeItemId ? getItem(activeItemId) : null}
                  bidDocs={bidDocs}
                  profileDocs={profileDocs}
                  branding={branding}
                  setBranding={setBranding}
                  onUploadForItem={uploadForItem}
                  onUploadFilesForItem={uploadFilesForItem}
                  onDownloadItemFile={downloadItemFile}
                  onDownloadFile={downloadFile}
                  onViewFile={viewFile}
                  onRemoveFile={removeFile}
                  onToggleRemove={toggleRemove}
                  onAttachVault={attachVault}
                  onPromoteItemToProfile={promoteItemToProfile}
                  onLoadEvidenceToEditor={loadEvidenceToEditor}
                  onLoadAttachedDocToEditor={loadAttachedDocToEditor}
                  onApplyBrandingToKeys={applyBrandingToKeys}
                  onApplyBrandingInline={() => applyBrandingInline(activeItemId)}
                  editorPdfKey={editorPdfKey}
                  onAddNewDoc={addNewDoc}
                  onMergeItemFiles={mergeItemFiles}
                  onRenameFile={renameFile}
                  onClickEvidence={onClickEvidence}
                  onSetErr={setErr}
                />
              </div>
              {/* Editor — takes all remaining space */}
              <div style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
                <EditorPane
                  editor={editor}
                  activeItemId={activeItemId}
                  docTitle={docTitle}
                  setDocTitle={setDocTitle}
                  profileDocs={profileDocs}
                  onSaveDraft={saveDraft}
                  onSaveDraftAsPdf={saveDraftAsPdf}
                  onInsertVaultImage={insertVaultImage}
                  editorPdfUrl={editorPdfUrl}
                  editorPdfKey={editorPdfKey}
                  onSetEditorPdfUrl={(url) => { setEditorPdfUrl(url); if (!url) setEditorPdfKey(""); }}
                  activeItem={activeItemId ? getItem(activeItemId) : null}
                  onSetErr={setErr}
                />
              </div>
            </div>
          </div>

        </>
      )}

      {/* Consolidation panel modal */}
      {showConsolidate && (
        <ConsolidationPanel
          reqs={activeList}
          items={items}
          getItem={getItem}
          onMergeKeys={mergeKeys}
          onRenderAndMerge={renderAndMerge}
          onDownloadFile={downloadFile}
          onClose={() => setShowConsolidate(false)}
          onSetErr={setErr}
          busy={consolidateBusy}
        />
      )}
    </div>
  );
}
